import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { FeeSchedule, FeeScheduleSaveRequest, FeeScheduleWithUsage, GUID, Tenant } from '@ndvr/billing-api';

import { BillingApi } from 'API';
import { useSelectedTenantGUIDs } from 'views/auth/tenant-selection';
import { Toast } from 'widgets';

const FeeScheduleQueryKeys = {
    all: ['feeSchedules'] as const,
    byTenants: (tenantGUIDs: GUID<Tenant>[]) => ['feeSchedules', 'tenants', tenantGUIDs],
} as const;

/** The fee schedules of the given tenants; the ones selected in the Hub by default. */
export const useFeeSchedules = (forTenantGUIDs?: GUID<Tenant>[]) => {
    const selectedTenantGUIDs = useSelectedTenantGUIDs();
    const tenantGUIDs = forTenantGUIDs ?? selectedTenantGUIDs;

    return useQuery({
        queryKey: FeeScheduleQueryKeys.byTenants(tenantGUIDs),
        enabled: tenantGUIDs.length > 0,
        queryFn: async (): Promise<FeeScheduleWithUsage[]> => {
            const [response] = await BillingApi().getFeeSchedulesForTenants(tenantGUIDs);
            return response ?? [];
        },
    });
};

export const useCreateFeeSchedule = (tenantGUID: GUID<Tenant>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request: FeeScheduleSaveRequest) =>
            BillingApi({ hideError: true }).createFeeSchedule(tenantGUID, request),
        onSuccess: () => {
            toast.success('Fee schedule created');
            queryClient.invalidateQueries({ queryKey: FeeScheduleQueryKeys.all });
        },
        onError: (error) => Toast.forError(error, 'Failed to create fee schedule'),
    });
};

export const useUpdateFeeSchedule = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            feeScheduleGUID,
            request,
        }: {
            feeScheduleGUID: GUID<FeeSchedule>;
            request: FeeScheduleSaveRequest;
        }) => BillingApi({ hideError: true }).updateFeeSchedule(feeScheduleGUID, request),
        onSuccess: () => {
            toast.success('Fee schedule updated');
            queryClient.invalidateQueries({ queryKey: FeeScheduleQueryKeys.all });
        },
        onError: (error) => Toast.forError(error, 'Failed to update fee schedule'),
    });
};

export const useDeleteFeeSchedule = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (feeScheduleGUID: GUID<FeeSchedule>) =>
            BillingApi({ hideError: true }).deleteFeeSchedule(feeScheduleGUID),
        onSuccess: () => {
            toast.success('Fee schedule deleted');
            queryClient.invalidateQueries({ queryKey: FeeScheduleQueryKeys.all });
        },
        onError: (error) => Toast.forError(error, 'Failed to delete fee schedule'),
    });
};
